import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

/**
 * Saying something to a screen reader that has nowhere visible to go.
 *
 * Feedback belongs on the object it is about, and most of the time the object is on the screen and
 * says it for itself. What is left over — a sign-out that is under way, a retry that has started —
 * has no object, and without this it would simply be silence for anybody not looking at the page.
 *
 * Polite on purpose: nothing here is worth interrupting somebody mid-sentence for.
 */
type Announce = (message: string) => void

const AnnounceContext = createContext<Announce>(() => {})

/** Usable anywhere. Outside an {@link Announcements} it does nothing, which is the right nothing. */
export function useAnnounce(): Announce {
  return useContext(AnnounceContext)
}

/** How long the region is cleared for, so the same words twice are read out twice. */
const RESET_MS = 80

export function Announcements({ children }: { readonly children: ReactNode }) {
  const [message, setMessage] = useState('')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (timer.current !== null) {
        clearTimeout(timer.current)
      }
    },
    [],
  )

  const announce = useCallback<Announce>((next) => {
    if (timer.current !== null) {
      clearTimeout(timer.current)
    }
    setMessage('')
    timer.current = setTimeout(() => {
      setMessage(next)
    }, RESET_MS)
  }, [])

  return (
    <AnnounceContext value={announce}>
      {children}
      <p className="visually-hidden" role="status" aria-live="polite">
        {message}
      </p>
    </AnnounceContext>
  )
}
